import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { type TFunction } from 'i18next';
import { type ContainerGroups } from '@sr/types';
import { useProfileV2 } from 'core/lib/Profile';
import { WithCustomerPortalApi } from 'core/customerPortalApi';
import { getApiUrl } from '#lib/utils/environment';

export type GetContainerGroupsProperties = {
  selectedBusinessPartner: string;
  useGrandParentLocation?: boolean;
};

export type GetContainerGroupsResultType = {
  containers: ContainerGroups[];
} & UseQueryResult<ContainerGroups[]>;

export const useContainerGroups = WithCustomerPortalApi<
  ContainerGroups[],
  GetContainerGroupsResultType,
  GetContainerGroupsProperties
>(
  (fetchFn, { selectedBusinessPartner, useGrandParentLocation }) => {
    const { profile } = useProfileV2();
    const { t } = useTranslation();

    const query = useQuery({
      queryKey: [
        'container-groups',
        selectedBusinessPartner,
        profile?.language,
        useGrandParentLocation ?? false,
      ],

      queryFn: ({ signal }) =>
        fetchFn({
          url: getApiUrl() + '/articles/containers',
          options: {
            method: 'POST',
            signal,
            body: JSON.stringify({
              dwKey: selectedBusinessPartner,
              includeSiblings: true,
              useGrandParentLocation,
            }),
          },
        }),
    });

    return {
      containers: query.data ? translateContainerGroups(t, query.data) : [],
      ...query,
    };
  },
  { hookName: 'useContainerGroups' },
);

const translateContainerGroups = (t: TFunction, containers: ContainerGroups[]) => {
  return containers.map((container) => ({
    ...container,
    groupName: t(`rented-containers.use-container-groups.container.groups.${container.groupName}`),
    containers: container.containers,
  }));
};
